import React, { useEffect, ComponentType } from 'react'
import { Outlet, OutletProps } from 'react-router-dom'
import { createURLString } from '../utils/urls'
import Session from '../Session'
import { ACCESS_DENIED } from '../constants'
import { RedirectParam } from '../context'
import useAuth from '../useAuth'

interface RenderNoAuthProps {
  redirect?: string
  render?: ComponentType
  history?: History
  callback?: RedirectHandler
}

const RenderNoAuth = ({
  redirect = ACCESS_DENIED,
  render: Render,
  callback,
}: RenderNoAuthProps) => {
  useEffect(() => {
    if (callback) callback({ redirect: window.location.href })
    else {
      const validatedRedirect = createURLString(redirect)
      if (validatedRedirect) window.location.replace(validatedRedirect)
    }
  }, [callback, redirect])

  if (Render) {
    return <Render />
  }
  return null
}

/**
 * Callback function that should be used to handle a redirect from ProtectedElement that could be
 * due to a session being either not authenticated or not authorized for a given path
 *
 * @param redirect - the redirect object that describes the route where access was attempted
 */
export type RedirectHandler = (redirect: RedirectParam) => void

/**
 * Props for ProtectedElement
 */
export interface BaseProtectedElementProps<
  ADGroups extends string[] = string[],
  Path extends string = string
> extends OutletProps {
  /**
   * an array of AD groups that are allowed to access this `path`
   * @note values must be the common name (CN) value only
   * @note users can access the path if they have *at least* one of the listed allowed AD groups
   * @note providing an empty array will check for authentication only
   * @defaultValue [] (an empty array)
   */
  allowed?: ADGroups

  /** a valid Session instance
   * @see {Session}
   * @defaultValue the current session in AuthContext
   */
  session?: Session | null

  /**
   * callback function that is called when given session is not authenticated
   * @defaultValue current AuthContext login function
   */
  onUnauthenticated?: RedirectHandler

  /** Component to render if given session is not authenticated */
  renderUnauthenticated?: ComponentType

  /**
   * URL to redirect the user when current session is not authenticated
   * @defaultValue https://logonservices.iam.target.com/login/responses/accessdenied.html
   */
  unauthenticatedRoute?: Path

  /** callback function that is called when given session is not authorized */
  onUnauthorized?: RedirectHandler

  /** Component to render if given session is not authenticated */
  renderUnauthorized?: ComponentType

  /**
   * URL to redirect the user when current session is not authorized against given `allowed` prop
   * @defaultValue https://logonservices.iam.target.com/login/responses/accessdenied.html
   */
  unauthorizedRoute?: Path
}

// https://github.com/DefinitelyTyped/DefinitelyTyped/blob/6b280e457f09ab6c9617ad9f124aa02b7136cb84/types/react-router/index.d.ts#L92-L94
type PassThroughOutletProps = Partial<OutletProps>
type ContentRenderProps = {
  component:
    | React.ComponentType<PassThroughOutletProps>
    | React.ComponentType<any>
  render: (props: PassThroughOutletProps) => React.ReactNode
  children:
    | ((props: PassThroughOutletProps) => React.ReactNode)
    | React.ReactNode
}

/**
 * Utility type that allows only a single key for an object.
 */
type PickOne<T> = {
  [P in keyof T]: Record<P, T[P]> &
    Partial<Record<Exclude<keyof T, P>, undefined>>
}[keyof T]

export type ProtectedElementProps<
  ADGroups extends string[] = string[],
  Path extends string = string
> = BaseProtectedElementProps<ADGroups, Path> &
  Partial<PickOne<ContentRenderProps>>

/**
 * Checks authentication and authorization against the AuthContext and its current session
 *
 * @component
 * @example
 * import { Route } from 'react-router-dom'
 * import { ProtectedElement } from '@praxis/component-auth'
 * import { MyComponent } from './MyComponent'
 *
 * <Route path="/protected" element={<ProtectedElement allowed={['MY-AD-GROUP']} />}>
 *   <MyComponent />
 * </Route>
 */
export const ProtectedElement = ({
  allowed,
  session,
  onUnauthenticated,
  renderUnauthenticated,
  unauthenticatedRoute = ACCESS_DENIED,
  onUnauthorized,
  renderUnauthorized,
  unauthorizedRoute = ACCESS_DENIED,
  context,
  component,
  render,
  children,
}: ProtectedElementProps) => {
  const auth = useAuth()

  onUnauthenticated =
    onUnauthenticated ||
    (renderUnauthenticated || auth.noProvider ? () => {} : auth.login)
  onUnauthorized = onUnauthorized || (renderUnauthorized ? () => {} : undefined)
  session = session || (auth.noProvider ? undefined : auth.session)

  const authenticated =
    session && session instanceof Session && session.isAuthenticated()
  if (!authenticated) {
    return (
      <RenderNoAuth
        redirect={unauthenticatedRoute}
        callback={onUnauthenticated}
        render={renderUnauthenticated}
      />
    )
  }

  if (allowed) {
    const authorized = authenticated && session!.isAuthorized(allowed)
    if (!authorized) {
      return (
        <RenderNoAuth
          redirect={unauthorizedRoute}
          callback={onUnauthorized}
          render={renderUnauthorized}
        />
      )
    }
  }

  /*
    Resources:
    - https://stackoverflow.com/a/48152635/14401479
    - https://github.com/remix-run/react-router/blob/v5.3.1/packages/react-router/modules/Route.js#L56-L72
  */
  if (children) {
    if (typeof children === 'function') {
      return <>{children({ context })}</>
    }
    return <>{children}</>
  }

  if (component) {
    return React.createElement(component, { context })
  }

  if (render) {
    return <>{render({ context })}</>
  }

  return <Outlet context={context} />
}
